const CHUNK_RELOAD_PARAM = 'bxr';
const CHUNK_RELOAD_STARTED_PARAM = 'bxrt';
// Each retry waits as long as the error page has been showing, within these bounds. Page load time
// counts too, so it's roughly 5s, 6s, 12s, 25s, 50s, then every 60s. Not immediate: some incidents
// still fail a retry at +3s.
const AUTO_RETRY_MIN_DELAY_MS = 5_000;
const AUTO_RETRY_MAX_DELAY_MS = 60_000;
const AUTO_RETRY_WINDOW_MS = 5 * 60 * 1000;
const LOAD_ERROR_PAGE_ID = 'bx-load-error-page';
const STORAGE_BLOCKED_PAGE_ID = 'bx-storage-blocked-page';

/**
 * True for a failed dynamic import of a lazy chunk (route chunk deleted by a newer deployment).
 * Message text varies by bundler/browser - webpack throws a named ChunkLoadError, native ESM
 * imports (esbuild/native-federation) throw a plain TypeError with browser-specific wording.
 */
export function isChunkLoadError(error: unknown): boolean {
    const err = error as { name?: string; message?: string } | undefined;
    const message = err?.message ?? String(error ?? '');
    return (
        err?.name === 'ChunkLoadError' ||
        message.includes('Failed to fetch dynamically imported module') ||
        message.includes('error loading dynamically imported module') ||
        message.includes('Importing a module script failed') ||
        message.includes('Loading chunk') ||
        // native-federation's init.mjs doFetch throws a plain TypeError shaped "<status>  <url>"
        // on a failed remote chunk fetch (e.g. a route chunk deleted by a newer MFE deployment)
        /^\d{3}\s+https?:\/\//.test(message) ||
        // es-module-shims' doFetch wraps any module fetch failure (DNS, connection reset, a
        // blocked/dropped request - what Frontdoor produces mid-propagation) as "Unable to fetch
        // <url> ...". Require a .js/.mjs URL right after "Unable to fetch" rather than matching
        // the bare phrase - the global unhandledrejection listener in bootstrap.ts treats any
        // match here as reload-worthy, so a generic match could swallow an unrelated failed API
        // call and force a page reload that loses whatever the user was doing.
        /Unable to fetch \S+\.m?js\b/.test(message) ||
        // Native ESM bare-specifier resolution failures - thrown when a module's import map is
        // missing an entry, or (BX-23098) when the import map itself wasn't honoured in time or
        // a specifier got blocked by a duplicate-key conflict. Wording is browser-specific:
        // Chrome:  Failed to resolve module specifier "x". Relative references must start with...
        // Safari:  Module name, "x" does not resolve to a valid URL.
        // Firefox: The specifier "x" was a bare specifier, but was not remapped to anything...
        // Chrome (import-map merge conflict): ...matches with "x" but is blocked by a null value
        message.includes('Failed to resolve module specifier') ||
        message.includes('does not resolve to a valid URL') ||
        message.includes('was a bare specifier, but was not remapped to anything') ||
        message.includes('blocked by a null value')
    );
}

/**
 * True for a SecurityError caused by a blocked storage partition - e.g. Safari's "Block all
 * cookies" setting, which blocks sessionStorage/localStorage entirely (getItem/setItem both
 * throw, verified live), not just document.cookie. SecurityError is also the DOMException name
 * for unrelated security-boundary violations (cross-origin frame/canvas access, postMessage,
 * etc.), and this classifier runs from app-wide catch points (ChunkErrorHandler's global
 * ErrorHandler, RouterStateService's navigation errors), not just the initial bootstrap import -
 * so name-matching alone would misclassify an unrelated SecurityError from anywhere else in the
 * app as "storage blocked" and hijack the UI with the wrong full-page takeover. Confirm storage
 * is actually inaccessible right now via a live probe before trusting the error's name.
 */
export function isStorageBlockedError(error: unknown): boolean {
    const err = error as { name?: string } | undefined;
    if (err?.name !== 'SecurityError') {
        return false;
    }
    try {
        sessionStorage.setItem('__bx_storage_probe__', '1');
        sessionStorage.removeItem('__bx_storage_probe__');
        return false;
    } catch {
        return true;
    }
}

/**
 * Reloads once on a chunk load failure - guards against a reload loop if the new build is also
 * broken. Uses a URL query-param marker rather than sessionStorage: in Safari private browsing,
 * sessionStorage.getItem can succeed while setItem silently throws, so a storage-backed guard
 * would never actually persist and would reload forever. A URL round-trip has no such asymmetry.
 * Returns true if a reload was triggered, false if we already tried once, so the caller can fall
 * back to showing an error instead of reloading forever.
 */
export function reloadOnceForChunkError(): boolean {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has(CHUNK_RELOAD_PARAM)) {
        return false;
    }
    searchParams.set(CHUNK_RELOAD_PARAM, '1');
    window.location.replace(`${window.location.pathname}?${searchParams}${window.location.hash}`);
    return true;
}

/**
 * Schedules a full-page reload with backoff, as long as we're still within 5 minutes of the
 * first failure - Frontdoor can take a while to finish propagating to all POPs, so a single
 * automatic reload (see reloadOnceForChunkError) isn't always enough. Reloading on a fixed timer
 * rather than reacting to anything means each retry re-runs this whole module from scratch, so
 * the "first failure" timestamp is stashed in the URL (same reasoning as CHUNK_RELOAD_PARAM -
 * a URL round-trip survives Safari private browsing, where sessionStorage silently doesn't).
 * Returns true if a retry was scheduled, false once the 5-minute window has elapsed, so the
 * caller can stop telling the user we're still checking.
 */
function scheduleAutoRetry(): boolean {
    const searchParams = new URLSearchParams(window.location.search);
    const now = Date.now();

    // Treat a missing or invalid first-failure timestamp as "starting now"
    const startedAt = parseStartedAt(searchParams, now) ?? now;
    const elapsed = now - startedAt;

    if (elapsed >= AUTO_RETRY_WINDOW_MS) {
        return false;
    }

    if (searchParams.get(CHUNK_RELOAD_STARTED_PARAM) !== String(startedAt)) {
        searchParams.set(CHUNK_RELOAD_STARTED_PARAM, String(startedAt));
        window.history.replaceState({}, '', `${window.location.pathname}?${searchParams}${window.location.hash}`);
    }

    const delay = Math.min(Math.max(elapsed, AUTO_RETRY_MIN_DELAY_MS), AUTO_RETRY_MAX_DELAY_MS);
    setTimeout(() => window.location.reload(), delay);
    return true;
}

/**
 * bxrt is user-editable URL state, not a trusted value - a tampered or corrupted timestamp
 * (missing, non-numeric, or in the future) must not let Date.now() - startedAt go negative, which
 * would keep the incident "within the window" forever and retry past the 5-minute bound.
 */
function parseStartedAt(searchParams: URLSearchParams, now: number): number | null {
    const parsed = Number(searchParams.get(CHUNK_RELOAD_STARTED_PARAM));
    return Number.isFinite(parsed) && parsed > 0 && parsed <= now ? parsed : null;
}

/** The URL params that hold a chunk-load incident - removed once the app has loaded (see chunk-load-recovery.ts). */
export const CHUNK_LOAD_INCIDENT_PARAMS = [CHUNK_RELOAD_PARAM, CHUNK_RELOAD_STARTED_PARAM] as const;

/**
 * The chunk-load incident this page load is part of, or null if there isn't one. `id` is the time
 * the error page was first shown (bxrt), or null if the incident hasn't got that far - i.e. the
 * automatic reload is the only retry so far.
 */
export function getChunkLoadIncident(): { id: number | null } | null {
    const searchParams = new URLSearchParams(window.location.search);
    if (!CHUNK_LOAD_INCIDENT_PARAMS.some((param) => searchParams.has(param))) {
        return null;
    }
    return { id: parseStartedAt(searchParams, Date.now()) };
}

/**
 * Renders a full-page "update in progress" takeover, in plain DOM rather than as an Angular
 * component so it works whether Angular has finished bootstrapping or not: `main-mfe.ts` calls it
 * when federation init itself fails (Angular never started), while RouterStateService and
 * bootstrap.ts's unhandledrejection listener call it when a lazy chunk fails after Angular is
 * already running - both are the same underlying condition (Frontdoor still propagating a new
 * deployment to all POPs), so both get the same messaging. Fixed-position + max z-index so it
 * covers the app underneath rather than just appending below it.
 */
export function showChunkLoadErrorPage(): void {
    if (document.getElementById(LOAD_ERROR_PAGE_ID)) {
        return;
    }

    const preloader = document.getElementById('preloader');
    if (preloader) {
        preloader.style.display = 'none';
    }

    const isRetrying = scheduleAutoRetry();

    const container = document.createElement('div');
    container.id = LOAD_ERROR_PAGE_ID;
    container.style.cssText =
        "position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#f6f6f6;font-family:'Roboto',Helvetica,Arial,sans-serif;color:#1b1e20;text-align:center;padding:24px;gap:28px;";
    container.innerHTML = `
        <style>
            @keyframes bx-load-error-pulse { 0%, 100% { opacity: 0.35; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1); } }
            @media (prefers-reduced-motion: reduce) { .bx-load-error-dot { animation: none !important; opacity: 0.7; } }
        </style>
        <img src="assets/images/BX-logo-hz-colour-RGB.png" alt="Buildxact" style="height:28px;width:auto;" />
        <div style="width:100%;max-width:420px;background:#fff;border:1px solid #e1e3e3;border-radius:16px;box-shadow:0 1px 2px rgba(27,30,32,0.04),0 12px 32px -8px rgba(27,30,32,0.12);padding:40px 32px 32px;display:flex;flex-direction:column;align-items:center;gap:8px;">
            <div style="width:64px;height:64px;border-radius:50%;background:#e5f1ee;display:flex;align-items:center;justify-content:center;margin-bottom:12px;">
                <svg viewBox="0 0 24 24" fill="none" width="30" height="30" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 12a8 8 0 1 1-2.34-5.66" stroke="#036856" stroke-width="2.2" stroke-linecap="round" />
                    <path d="M20 4v5h-5" stroke="#036856" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </div>
            <h2 style="font-size:20px;font-weight:600;line-height:1.3;margin:0;letter-spacing:-0.01em;">Buildxact is being updated</h2>
            <p style="font-size:14.5px;line-height:1.55;color:#676c71;margin:0;max-width:34ch;">We're rolling out an update right now. No need to keep refreshing &mdash; this page will pick up the new version on its own.</p>
            <div style="display:flex;flex-direction:column;align-items:center;gap:14px;margin-top:12px;width:100%;">
                <button id="bx-load-error-refresh" style="appearance:none;border:none;cursor:pointer;font:inherit;font-weight:600;font-size:14.5px;color:#fff;background:#036856;padding:11px 28px;border-radius:8px;box-shadow:0 1px 2px rgba(3,104,86,0.15);">Refresh now</button>
                ${
                    isRetrying
                        ? `<div style="display:flex;align-items:center;gap:7px;font-size:12.5px;color:#676c71;">
                        <span class="bx-load-error-dot" style="width:6px;height:6px;border-radius:50%;background:#036856;animation:bx-load-error-pulse 1.8s ease-in-out infinite;flex-shrink:0;"></span>
                        Checking again shortly
                    </div>`
                        : ''
                }
            </div>
        </div>
        <div style="font-size:13px;line-height:1.6;color:#676c71;">
            Still stuck after a few tries? Try closing and reopening this browser tab.<br />
            Need help? <a href="https://www.buildxact.com/support-overview" target="_blank" rel="noopener noreferrer" style="color:#30637b;font-weight:500;">Contact support</a>.
        </div>
    `;

    const refreshButton = container.querySelector<HTMLButtonElement>('#bx-load-error-refresh');
    refreshButton?.addEventListener('click', () => {
        refreshButton.textContent = 'Refreshing…';
        location.reload();
    });

    document.body.appendChild(container);
}

/**
 * Renders a full-page takeover for a storage-blocked browser (see isStorageBlockedError) - plain
 * DOM for the same reason as showChunkLoadErrorPage: this can run before Angular has bootstrapped.
 * Deliberately has no reload button and no auto-retry - unlike a stale deployment, reloading can
 * never fix a browser privacy setting, so offering it would just be misleading.
 */
export function showStorageBlockedErrorPage(): void {
    if (document.getElementById(STORAGE_BLOCKED_PAGE_ID)) {
        return;
    }

    const preloader = document.getElementById('preloader');
    if (preloader) {
        preloader.style.display = 'none';
    }

    const container = document.createElement('div');
    container.id = STORAGE_BLOCKED_PAGE_ID;
    container.style.cssText =
        "position:fixed;inset:0;z-index:2147483647;display:flex;flex-direction:column;align-items:center;justify-content:center;background:#f6f6f6;font-family:'Roboto',Helvetica,Arial,sans-serif;color:#1b1e20;text-align:center;padding:24px;gap:28px;";
    container.innerHTML = `
        <img src="assets/images/BX-logo-hz-colour-RGB.png" alt="Buildxact" style="height:28px;width:auto;" />
        <div style="width:100%;max-width:420px;background:#fff;border:1px solid #e1e3e3;border-radius:16px;box-shadow:0 1px 2px rgba(27,30,32,0.04),0 12px 32px -8px rgba(27,30,32,0.12);padding:40px 32px 32px;display:flex;flex-direction:column;align-items:center;gap:8px;">
            <div style="width:64px;height:64px;border-radius:50%;background:#fdeeea;display:flex;align-items:center;justify-content:center;margin-bottom:12px;">
                <svg viewBox="0 0 24 24" fill="none" width="30" height="30" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 3l9 16H3l9-16z" stroke="#c0392b" stroke-width="2" stroke-linejoin="round" />
                    <path d="M12 10v4" stroke="#c0392b" stroke-width="2" stroke-linecap="round" />
                    <circle cx="12" cy="17" r="1" fill="#c0392b" />
                </svg>
            </div>
            <h2 style="font-size:20px;font-weight:600;line-height:1.3;margin:0;letter-spacing:-0.01em;">Buildxact can't run in this browser</h2>
            <p style="font-size:14.5px;line-height:1.55;color:#676c71;margin:0;max-width:34ch;">Your browser is blocking this site from storing data, which Buildxact needs in order to run.</p>
            <div style="text-align:left;width:100%;background:#f6f6f6;border-radius:10px;padding:16px 18px;margin-top:12px;font-size:13.5px;line-height:1.6;color:#1b1e20;">
                <strong>If you're using Safari:</strong>
                <ol style="margin:6px 0 0;padding-left:18px;">
                    <li>Open Safari &rarr; Settings &rarr; Advanced &rarr; Privacy</li>
                    <li>Turn off &ldquo;Block all cookies&rdquo;</li>
                    <li>Reload this page</li>
                </ol>
            </div>
            <p style="font-size:12.5px;line-height:1.5;color:#676c71;margin:10px 0 0;max-width:34ch;">Using a different browser? Check its privacy or cookie settings for this site.</p>
        </div>
        <div style="font-size:13px;line-height:1.6;color:#676c71;">
            Need help? <a href="https://www.buildxact.com/support-overview" target="_blank" rel="noopener noreferrer" style="color:#30637b;font-weight:500;">Contact support</a>.
        </div>
    `;

    document.body.appendChild(container);
}
